import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateOnGoBack } from "./useUpdateOnGoBack";
import { useUpdateOnGoHome } from "./useUpdateOnGoHome";
import { useUpdateOnMinimizar } from "./useUpdateOnMinimizar";

/**
 * Hook que agrega todas as funções de navegação do aplicativo. Administra uma função de cleanup para quando o aplicativo é fechado sem que seja minimizado
 * @param {Object} params
 * @param {import("./useUpdateOnGoBack").Callback} params.onGoBack Callback executado quando o usuário clica no botão de voltar da NavigationBar
 * @param {import("./useUpdateOnGoBack").Options} [params.onGoBackOptions]
 * @param {import("./useUpdateOnGoHome").Callback} [params.onGoHome] Callback executado quando o usuário clica no botão de voltar para a home da NavigationBar
 * @param {import("./useUpdateOnGoHome").Options} [params.onGoHomeOptions]
 * @param {import("./useUpdateOnMinimizar").Callback} [params.onMinimizar] Callback executado quando o usuário minimiza o aplicativo
 * @param {import("./useUpdateOnMinimizar").Options} [params.onMinimizarOptions]
 * @param {() => void} [params.onUnmount] Callback executado quando o componente é desmontado sem ser minimizado. Util para se passar uma função de cleanup.
 *
 *
 */
export function useNavigationBar({
    onGoBack,
    onGoBackOptions = {},
    onGoHome = () => {},
    onGoHomeOptions = {},
    onMinimizar = () => {},
    onMinimizarOptions = {},
    onUnmount = () => {}
}) {
    const minimizando = useRef(false);

    const handleUnmount = useCallback(() => {
        if (minimizando.current) return;
        minimizando.current = false;
        return onUnmount();
    }, [minimizando.current]);

    useUpdateOnGoBack(() => {
        minimizando.current = false;
        return onGoBack();
    }, onGoBackOptions);
    useUpdateOnGoHome(() => {
        minimizando.current = false;
        return onGoHome();
    }, onGoHomeOptions);
    useUpdateOnMinimizar(() => {
        minimizando.current = true;
        return onMinimizar();
    }, onMinimizarOptions);

    useEffect(() => {
        return () => {
            handleUnmount();
        };
    }, [minimizando.current]);
}
